import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ndaAPI, translatorAPI } from '@/utils/api';
import { toast } from 'sonner';
import { FileCheck, CheckCircle, AlertCircle } from 'lucide-react';

const TranslatorNDA = () => {
  const { user } = useOutletContext();
  const [profile, setProfile] = useState(null);
  const [ndaTemplate, setNdaTemplate] = useState(null);
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [profileRes, ndaRes] = await Promise.all([
        translatorAPI.getProfile(),
        ndaAPI.getTemplate()
      ]);
      
      setProfile(profileRes.data);
      setNdaTemplate(ndaRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load NDA');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSign = async () => {
    if (!typedName.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    
    if (!agreed) {
      toast.error('Please agree to the terms');
      return;
    }
    
    setSigning(true);
    try {
      await ndaAPI.signNDA({ typed_name: typedName, agreed: true });
      toast.success('NDA signed successfully!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign NDA');
    } finally {
      setSigning(false);
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading NDA...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-heading font-bold text-primary mb-2">Non-Disclosure Agreement</h1>
          <p className="text-muted-foreground">Review and sign the NDA to start receiving work assignments</p>
        </div>
        
        {profile?.nda_signed ? (
          <Card className="border-l-4 border-l-success bg-success/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-success flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-success mb-1">NDA Signed</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    You signed the NDA on {new Date(profile.nda_signed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Version: {profile.nda_version}
                  </p>
                  {profile.nda_signed_pdf && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(ndaAPI.downloadNDA(profile.nda_signed_pdf), '_blank')}
                      data-testid="btn-download-nda"
                    >
                      Download Signed PDF
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="border-l-4 border-l-warning bg-warning/5">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-warning mt-0.5" />
                  <div>
                    <p className="font-medium text-warning">Signature Required</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      You must sign this NDA before you can be assigned any work. Please read carefully and sign below.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-accent" />
                  NDA Document
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="prose prose-sm max-w-none bg-secondary/30 p-6 rounded-lg border border-border"
                  dangerouslySetInnerHTML={{ __html: ndaTemplate?.content || '' }}
                />
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Sign NDA</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="typed_name">Type your full legal name *</Label>
                  <Input
                    id="typed_name"
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="Enter your full name"
                    className="text-xl font-heading italic"
                    data-testid="input-nda-name"
                  />
                  <p className="text-sm text-muted-foreground">
                    This will serve as your digital signature
                  </p>
                </div>
                
                <div className="flex items-start space-x-3 p-4 bg-secondary/30 rounded-lg">
                  <Checkbox
                    id="agreed"
                    checked={agreed}
                    onCheckedChange={setAgreed}
                    data-testid="checkbox-nda-agree"
                  />
                  <Label htmlFor="agreed" className="cursor-pointer text-sm leading-relaxed">
                    I have read and understood the terms of this Non-Disclosure Agreement. 
                    I agree to be bound by its terms and conditions. I understand that any 
                    breach of this agreement may result in legal action and immediate termination.
                  </Label>
                </div>
                
                <Button
                  onClick={handleSign}
                  disabled={signing || !typedName.trim() || !agreed}
                  size="lg"
                  className="w-full bg-accent hover:bg-accent/90"
                  data-testid="btn-sign-nda"
                >
                  {signing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Signing NDA...
                    </>
                  ) : (
                    <>
                      <FileCheck className="w-5 h-5 mr-2" />
                      Sign NDA
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TranslatorNDA;
